import type * as z from 'zod';

import { report } from '@shared/observability';

import { getAppId, pendingAppId } from './app-id';
import { publish } from './bus';
import { catalog, type EventName, type EventParams } from './catalog';
import type { EventDefinition } from './catalog';
import { buildEnvelope } from './context';
import {
	contentSchema,
	envelopeSchema,
	type Envelope,
	type EventContent,
} from './envelope';

/**
 * A recorded event: one flat, self-describing row. Envelope, content context
 * and the event's own parameters share one namespace (the catalog test
 * guards against a clash), so the row can be sent or stored as it is.
 */
export type ReaderEvent<N extends EventName = EventName> = {
	[K in N]: { name: K } & Envelope &
		z.output<typeof contentSchema> &
		OwnParams<z.output<(typeof catalog)[K]['params']>>;
}[N];

// An event without parameters infers as `Record<string, never>`, which
// intersected with the rest would forbid every other field of the row.
type OwnParams<P> = string extends keyof P ? unknown : P;

export class InvalidEventError extends Error {
	constructor(
		readonly event: string,
		readonly problems: ReadonlyArray<string>,
	) {
		super(`Invalid "${event}" event: ${problems.join('; ')}`);
		this.name = 'InvalidEventError';
	}
}

function problemsOf(error: z.ZodError): Array<string> {
	return error.issues.map(
		(issue) => `${issue.path.join('.') || '(event)'}: ${issue.message}`,
	);
}

/**
 * Validates an event and returns its row, or the reasons it is not valid.
 * Exported for tests and tools; app code calls `track()`.
 */
export function buildEvent<N extends EventName>(
	name: N,
	params: EventParams<N>,
	content: EventContent = {},
): { event: ReaderEvent<N> } | { problems: Array<string> } {
	// `Object.hasOwn`: a name such as "constructor" must not find a property
	// the catalog object inherits.
	const definition: EventDefinition | undefined = Object.hasOwn(catalog, name)
		? catalog[name]
		: undefined;
	if (!definition) {
		return { problems: [`"${String(name)}" is not in the catalog`] };
	}

	const parsedParams = definition.params.safeParse(params);
	const parsedContent = contentSchema.safeParse(content);
	const problems = [
		...(parsedParams.success ? [] : problemsOf(parsedParams.error)),
		...(parsedContent.success ? [] : problemsOf(parsedContent.error)),
	];
	if (!parsedParams.success || !parsedContent.success) {
		return { problems };
	}

	const missing = definition
		.requires(parsedParams.data)
		.filter((key) => parsedContent.data[key] === undefined);
	if (missing.length) {
		return {
			problems: missing.map((key) => `${key}: required by "${name}"`),
		};
	}

	const envelope = envelopeSchema.safeParse(buildEnvelope());
	if (!envelope.success) {
		return { problems: problemsOf(envelope.error) };
	}

	// A field that does not apply is left out of the row, never present as
	// `undefined`: the row is sent and stored as it is.
	const row = Object.fromEntries(
		Object.entries({
			name,
			...envelope.data,
			...parsedContent.data,
			...parsedParams.data,
		}).filter(([, value]) => value !== undefined),
	);
	return { event: row as ReaderEvent<N> };
}

/**
 * Records a reader event: validates it against the catalog, stamps the
 * envelope and publishes it on the bus. `content` names the content the
 * event is about, which the call site must know; it is not read from the
 * route.
 *
 * An invalid event throws in dev and test, so it is caught where it is
 * written. In production it is dropped and reported through observability,
 * and never throws: recording must not be able to break the reader.
 */
export function track<N extends EventName>(
	name: N,
	params: EventParams<N>,
	content?: EventContent,
): void {
	let result: ReturnType<typeof buildEvent<N>>;
	try {
		result = buildEvent(name, params, content);
	} catch (error) {
		result = {
			problems: [error instanceof Error ? error.message : String(error)],
		};
	}

	if ('problems' in result) {
		if (import.meta.env.DEV) {
			throw new InvalidEventError(name, result.problems);
		}
		report({
			name: 'invalid_reader_event',
			payload: { event: name, problems: result.problems },
		});
		return;
	}

	record(result.event as ReaderEvent);
}

// Recorded while the app id is being fetched (a device's first launch),
// in order. They are published together once the request settles, stamped
// with the id if it arrived and without it if it did not: an event with no
// app is better than no event.
let held: Array<ReaderEvent> = [];

function record(event: ReaderEvent): void {
	const pending = event.app_id === undefined ? pendingAppId() : undefined;
	if (!pending && !held.length) {
		publish(event);
		return;
	}
	held.push(event);
	if (held.length > 1) {
		return;
	}
	const release = () => {
		const appId = getAppId();
		const events = held;
		held = [];
		for (const waiting of events) {
			publish(
				appId === undefined || waiting.app_id !== undefined
					? waiting
					: { ...waiting, app_id: appId },
			);
		}
	};
	void (pending ?? Promise.resolve()).then(release);
}
